
import { useState } from 'react';
import { apiClient } from '@/utils/apiClient'; // @deprecated_supabase - Use apiClient instead of supabase
import { useToast } from '@/hooks/use-toast';
import { createAudioBlob, createAudioUrl } from '@/utils/audioUtils';
import { parseApiError } from '@/utils/errorUtils';
import { useAudioStorage } from '@/hooks/useAudioStorage';
import { estimateTokens, getRecommendedLatencyTolerance } from '@/utils/tokenEstimation';
import { sanitizeGeneratedNames } from '@/utils/nameSanitizer';
import { evaluateAnchorIntegration } from '@/utils/anchorQA';
import { generateMeditationAsync } from '@/utils/meditationApi';

import { logger } from '@/utils/logger';

export const useAudioGeneration = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null); // Backend-created session ID
  const [error, setError] = useState<any>(null);
  const [generatedContent, setGeneratedContent] = useState<string | null>(null);
  const { toast } = useToast();
  const { saveAudioToStorage } = useAudioStorage();

  const generateAudioFromPrompt = async (
    prompt: string,
    useExactText = false,
    includeBiography = false,
    userBiography = '',
    categoryId?: string,
    voiceGender?: string,
    sessionTitle?: string,
    voiceType?: string,
    sessionType?: string,
    latencyTolerance?: number,
    groupSession?: any,
    usePersonalBiography?: boolean,
    practitionerClient?: any,
    specific_voice_id?: string,
    practitionerProfile?: any, // Add practitioner profile parameter
    fastMode?: boolean,
    backgroundMusicFile?: string, // Add background music file parameter
    voiceTheraPreferences?: { includeIntroduction: boolean; introductionStyle: 'warm' | 'professional' | 'friendly' },
    sessionNotes?: string, // Add session notes
    sessionScope?: 'individual' | 'group' | 'personal', // Add session scope
    sessionLength?: number // Add session length parameter in seconds
  ) => {
    console.log('🎵 Starting audio generation with params:', {
      promptLength: prompt.length,
      useExactText,
      includeBiography,
      categoryId,
      voiceGender,
      voiceType,
      sessionTitle,
      hasGroupSession: !!groupSession,
      usePersonalBiography,
      hasPractitionerProfile: !!practitionerProfile,
      practitionerName: practitionerProfile?.first_name || practitionerProfile?.full_name?.split(' ')[0] || 'not provided',
      backgroundMusicFile,
      voiceTheraPreferences
    });

    try {
      setIsGenerating(true);
      setError(null);
      setGeneratedContent(null);
      setSessionId(null); // Reset session ID from previous generation
      setAudioUrl(null); // Reset audio URL from previous generation

      let finalText = prompt;
      let contentGenerated = false;
      let anchorQA: { anchorCount: number; anchorsUsed: string[] } | null = null;

      // Step 1: Generate content if not using exact text
      if (!useExactText) {
        logger.session('Generating content...');
        
        // Simple prompt optimization - just return the prompt as-is
        let enhancedPrompt = prompt;
        
        // Personalization is handled upstream; avoid re-personalizing here to prevent dilution


        // Calculate dynamic latency tolerance and estimate tokens
        const estimatedTokens = estimateTokens(enhancedPrompt);
        const dynamicLatencyTolerance = latencyTolerance || getRecommendedLatencyTolerance(
          sessionType || 'personalized',
          categoryId,
          includeBiography
        );
        
        console.log('🧠 Pre-generation analysis:', {
          estimatedTokens,
          dynamicLatencyTolerance,
          sessionType,
          categoryId
        });

console.log('🔄 Calling generate-meditation async job queue...');
try {
  // Get current user for practitioner_id using backend API
  const { data: { user: currentUser } } = await apiClient.getCurrentUser();

  // Validate practitioner-client session data
  if (sessionType === 'practitioner-client' && !practitionerClient?.id) {
    throw new Error('Practitioner client ID is required for practitioner-client sessions');
  }

  // Log session creation context for debugging
  console.log('📋 Session creation context:', {
    sessionType,
    practitionerId: currentUser?.id,
    clientId: practitionerClient?.id,
    sessionScope,
    hasPractitionerClient: !!practitionerClient,
  });

  const { data: aiData, error: aiError } = await generateMeditationAsync({
    form_type: sessionType || 'personalized',
    prompt: enhancedPrompt,
    latencyTolerance: dynamicLatencyTolerance,
    sessionType: sessionType || 'personalized',
    categoryId,
    category_id: categoryId, // Backend expects this field name
    title: sessionTitle, // Session title for searchable reference
    session_length: sessionLength || (() => {
      // Extract session length from prompt if available (fallback for non-practitioner sessions)
      const sessionLengthMatch = enhancedPrompt.match(/(\d+)\s*minutes?/i);
      return sessionLengthMatch ? parseInt(sessionLengthMatch[1]) * 60 : 300; // Return seconds
    })(),
    groupSession,
    usePersonalBiography,
    voiceTheraPreferences: voiceTheraPreferences || { includeIntroduction: true, introductionStyle: 'warm' },
    // Backend session creation context
    practitioner_id: currentUser?.id,
    client_id: practitionerClient?.id,
    session_scope: sessionScope || (groupSession ? 'group' : (sessionType === 'practitioner-client' ? 'individual' : 'personal')),
    group_session_id: groupSession?.id,
    assigned_to: practitionerClient?.id, // Only use client ID, don't fall back to user ID
    session_notes: sessionNotes,
    client_biography: practitionerClient?.biography,
    // Required backend schema fields with sensible defaults
    environment: 'calm',
    voice_gender: voiceGender || 'female',
    meditation_style: 'guided',
    voice_style: voiceType || 'calm',
    seeking_goal: 'relaxation',
    desired_feeling: 'peaceful',
    include_background_sounds: !!backgroundMusicFile,
    background_music_file: backgroundMusicFile,
    // CRITICAL: Include specific voice for cache differentiation
    specific_voice_id: specific_voice_id,  // This ensures different voices get different cache keys
    voice_id: specific_voice_id,  // Also send as voice_id for backend compatibility
    allowedNames: (() => {
    logger.debug('Name policy evaluation', {
      usePersonalBiography,
      hasGroupSession: !!groupSession,
      participantCount: groupSession?.participants?.length,
      hasPractitionerClient: !!practitionerClient,
      hasPractitionerProfile: !!practitionerProfile
    }, 'NAME_POLICY');

      if (practitionerClient && sessionType === 'practitioner-client') {
        const clientName = (practitionerClient.first_name || '').trim();
        // Handle couples by splitting on "and" or "&" within the first_name field
        const names = clientName.includes(' and ')
          ? clientName.split(' and ').map(n => n.trim())
          : clientName.includes(' & ')
          ? clientName.split(' & ').map(n => n.trim())
          : [clientName].filter(Boolean);

        // Extract practitioner name for the policy
        let practitionerName = '';
        if (practitionerProfile?.first_name?.trim()) {
          practitionerName = practitionerProfile.first_name.trim();
        } else if (practitionerProfile?.full_name?.trim()) {
          // Use first word of full name if no dedicated first_name
          practitionerName = practitionerProfile.full_name.trim().split(' ')[0];
        }

        logger.debug('Practitioner-client names extracted', {
          clientNames: names,
          practitionerName
        }, 'PRACTITIONER_CLIENT_NAMES');

        return {
          clientNames: names,
          practitionerName: practitionerName || undefined,
          usage: 'practitioner-client'
        };
      }

      if (usePersonalBiography) {
        return {
          firstName: undefined, // Will be handled by backend
          preferredName: undefined, // Will be handled by backend
          usage: 'first'
        };
      }

      if (groupSession?.participants?.length > 0) {
        const groupNames = groupSession.participants
          .filter((p: any) => p.consentToName === true)  // FIXED: Require explicit consent (not just !== false)
          .map((p: any) => [p.firstName, p.preferredName].filter(Boolean))
          .flat()
          .filter(Boolean);

        logger.debug('Group names extracted (explicit consent only)', groupNames, 'GROUP_NAMES');

        return {
          groupNames,
          usage: 'group'
        };
      }

      logger.warn('Falling back to neutral policy', null, 'NAME_POLICY');
      return { usage: 'neutral' };
    })()
  }, {
    onProgress: (progress, message) => {
      console.log(`📊 Audio generation progress: ${Math.round(progress * 100)}% - ${message}`);
    }
  });

  if (aiError) {
    console.error('❌ Content generation function error:', aiError);
    throw new Error(`Content generation failed: ${aiError.message || JSON.stringify(aiError)}`);
  }

  if (!aiData?.content) {
    console.error('❌ No content received from function');
    throw new Error('No content was generated by the function');
  }

  finalText = aiData.content;
  // Backend already handles name sanitization with proper group policies
  console.log('✅ Using backend-sanitized content with proper group name handling');
  contentGenerated = true;
  setGeneratedContent(finalText);

  // Capture backend-created session ID and audio URL
  if (aiData.session_id) {
    console.log('✅ Backend created session:', aiData.session_id);
    setSessionId(aiData.session_id);
  }
  if (aiData.audio_url) {
    console.log('✅ Backend generated audio:', aiData.audio_url);
    setAudioUrl(aiData.audio_url);

    // Backend already generated audio - skip separate audio generation
    setIsGenerating(false);
    console.log('✅ Using backend-generated audio - skipping separate audio generation');
    return;
  }
  // Evaluate biography anchor integration (lightweight QA)
  if (includeBiography && userBiography) {
    anchorQA = evaluateAnchorIntegration(finalText, userBiography);
    console.log('📌 Biography anchors used:', anchorQA);
  }
  console.log('✅ Content generated successfully, length:', finalText.length);
} catch (functionError) {
  console.error('💥 Function invocation failed:', functionError);
  throw new Error(`Failed to generate content: ${functionError.message}`);
}
      }

      // Step 2: Select voice (only if backend didn't provide audio)
      console.log('🎤 Selecting voice for gender:', voiceGender, 'specific voice:', specific_voice_id);

      // Import voice utilities from useVoices hook (note: using import here for dynamic loading)
      const { useVoices } = await import('@/hooks/useVoices');

      // For now, we'll use a simplified approach - the backend handles voice ID resolution
      // The frontend just needs to pass the voice ID, and the backend VOICE_MAPPING will resolve it
      const selectedVoiceKey = specific_voice_id || (voiceGender === 'male' ? 'male_default' : 'female_default');
      console.log('🎤 Selected voice key:', selectedVoiceKey);
      
      const voiceConfig = {
        key: selectedVoiceKey,
        name: specific_voice_id || (voiceGender === 'male' ? 'Male Voice' : 'Female Voice'),
        settings: {}
      };
      logger.audio('Voice selected', { name: voiceConfig.name, key: voiceConfig.key });

      // Step 3: Generate audio with reliable fallbacks
      console.log('🔊 Generating audio with voice:', voiceConfig.key);

      const trimText = (text: string, limit = 2000) => {
        if (text.length <= limit) return text;
        const trimmed = text.slice(0, limit);
        const lastPeriod = trimmed.lastIndexOf('.');
        return lastPeriod > limit - 200 ? trimmed.slice(0, lastPeriod + 1) : trimmed;
      };

      let audioText = trimText(finalText);
      if (audioText.length < finalText.length) {
        console.log('✂️ Content trimmed for reliability');
        toast({ title: 'Content optimized', description: 'Shortened for reliable audio generation.' });
      }

      try {
        // Try primary voice using backend API
        let { data, error } = await apiClient.generateAudio({
          script: audioText,
          voice_id: voiceConfig.key,
          voice_gender: voiceGender,
          latency_tolerance: latencyTolerance
        });

        // If timeout or server indicated long processing, retry in Fast Mode (chunked)
        const shouldFastRetry = !!(error?.status === 408 || (data as any)?.timeout);
        if (shouldFastRetry) {
          console.log('⏱️ Fast Mode retry: chunked generation with longer timeout');
          const fastRetry = await apiClient.generateAudio({
            script: audioText,
            voice_id: voiceConfig.key,
            voice_gender: voiceGender,
            latency_tolerance: latencyTolerance,
            force_chunking: true,
            request_timeout_ms: 60000,
            chunk_max_chars: 900
          });
          data = fastRetry.data;
          error = fastRetry.error;
        }

        // Handle response data
        let audioUrl = data?.audioUrl || data?.audio_url;

        // Fallback to default voice if still failing
        if (error || !audioUrl) {
          console.log('🔄 Trying fallback voice...');
          const fallbackVoice = voiceGender === 'male' ? '1AFG9J83A2LoVyWOH779' : 'PB6BdkFkZLbI39GHdnbQ';
          const fallbackResult = await apiClient.generateAudio({
            script: audioText,
            voice_id: fallbackVoice,
            voice_gender: voiceGender,
            force_chunking: true,
            request_timeout_ms: 60000,
            chunk_max_chars: 900
          });
          if (fallbackResult.error) {
            throw new Error('Audio generation failed with both primary and fallback voices');
          }
          audioUrl = fallbackResult.data?.audioUrl || fallbackResult.data?.audio_url;
          if (!audioUrl) {
            throw new Error('Audio generation failed with both primary and fallback voices');
          }
          console.log('✅ Fallback voice successful');
        }

        if (!audioUrl) {
          throw new Error('No audio URL received from server');
        }

        console.log('✅ Audio generated successfully');
        console.log('✅ Using server-stored audio URL:', audioUrl);

        // Audio is already stored on server, use URL directly
        const finalAudioUrl = audioUrl;

        setAudioUrl(finalAudioUrl);
        console.log('🎉 Audio generation completed');

        toast({
          title: "Session Generated!",
          description: `Your ${voiceConfig.name} session is ready to play.`,
        });

      } catch (functionError) {
        console.error('Audio generation failed:', functionError);
        
        // If we have content but audio failed, still save the content
        if (contentGenerated && finalText) {
          setGeneratedContent(finalText);
        }
        
        throw new Error(`Failed to generate audio: ${functionError.message}`);
      }

    } catch (error: any) {
      console.error('💥 Audio generation error:', error);
      const appError = parseApiError(error, 'Audio generation');
      setError(appError);
      
      // Show user-friendly error message
      toast({
        title: "Audio Generation Failed",
        description: appError.message,
        variant: "destructive",
      });
      
      // Re-throw so retry system can catch it
      throw appError;
    } finally {
      setIsGenerating(false);
      console.log('🏁 Audio generation process finished');
    }
  };

  const resetAudio = () => {
    console.log('🔄 Resetting audio state');
    setAudioUrl(null);
    setSessionId(null);
    setError(null);
    setGeneratedContent(null);
  };

  return {
    isGenerating,
    audioUrl,
    sessionId, // Backend-created session ID
    error,
    generatedContent,
    generateAudioFromPrompt,
    resetAudio
  };
};
